import React, { useMemo, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl, MessageDescriptor } from "react-intl";
import sanitizeHtml from "sanitize-html";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { B100, N50 } from "@atlaskit/theme/colors";

import { messages } from "../messages";
import { setEditModeItem, updateStory } from "../state";
import { useAppDispatch, useAppSelector } from "../state/hook";

import { HiddenAriaDescription } from "./hidden-aria-description";
import { EditContainer, IconBlock, Input, Name } from "./shared.styles";
import { Story16IconCustom } from "./story-16-icon-custom";

/**
 * A JSX component for the story row.
 * @param param0
 * @returns A roadmap column story row.
 */
export const StoryRowItem = ({
    id,
    name,
    storyIndex,
    epicIndex,
    isEpicCol = true
}: EpicColumnRowPropsType) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const isDemoComplete = useAppSelector(
        ({ isDemoComplete }) => isDemoComplete
    );
    const editModeItemId = useAppSelector(
        ({ editModeItemId }) => editModeItemId
    );
    const [storyNameInputValue, setStoryInputName] = useState("");
    const [storyName, setStoryName] = useState(name);
    const [rowMode, setRowMode] = useState("UNKNOWN" as ContentMode);
    const storyLabelText = useMemo(
        () => intl.formatMessage(messages.story),
        []
    );
    const readOnlyTextValue = useMemo(
        () =>
            typeof storyName === "object"
                ? intl.formatMessage(storyName)
                : storyName,
        [storyName]
    );
    const activateToEditTextDescription = useMemo(
        () => intl.formatMessage(messages.activateToEditTextDescription),
        []
    );
    const activateToToggleMessageId = useMemo(
        () => `activateToToggleMessage-${id}`,
        [id]
    );
    React.useEffect(() => setStoryName(name), [name]);

    React.useEffect(
        () => setRowMode(editModeItemId === id ? "EDIT" : "VIEW"),
        [editModeItemId]
    );

    const updateStoryName = (name: string) => {
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapUpdateStory",
            eventType: "ui"
        }).fire();

        dispatch(updateStory({ storyIndex, epicIndex, storyName: name }));
        dispatch(setEditModeItem({ id: undefined }));
        setStoryName(name);
        setStoryInputName("");
        setRowMode("VIEW");
    };

    const toggleEditMode = () => {
        // this story row can only be edited if it is on the epic column.
        if (!isEpicCol) {
            return;
        }

        // the demo has not been complete so the user cannot edit any story / epic, skip.
        if (!isDemoComplete) {
            return;
        }

        // there is another item in edit mode, skip.
        if (editModeItemId) {
            return;
        }

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "interactiveRoadmapSetEditModeItem",
            eventType: "ui",
            attributes: {
                itemEditId: id
            }
        }).fire();

        dispatch(setEditModeItem({ id }));
        setRowMode("EDIT");

        if (typeof storyName === "object") {
            setStoryInputName(intl.formatMessage(storyName));
        } else {
            setStoryInputName(storyName);
        }
    };

    // auto focus on input element
    const containerRef = useRef(null as HTMLInputElement | null);
    const inputRef = useRef(null as HTMLInputElement | null);
    React.useEffect(() => {
        if (inputRef.current) {
            inputRef.current.onfocus = () => {
                if (containerRef.current) {
                    containerRef.current.style.borderColor = B100;
                }
            };

            inputRef.current.onblur = () => {
                if (containerRef.current) {
                    containerRef.current.style.borderColor = N50;
                }
            };

            inputRef.current.focus();
        }
    }, [rowMode, inputRef, containerRef]);

    const readOnlyContainerStyles = [
        isEpicCol &&
            css({
                backgroundColor: "transparent",
                border: "none",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                width: "100%",
                borderRadius: "4px",
                marginInlineStart: "57px;",
                transition: "transform 1s ease-in",
                maxWidth: "406px",
                gap: "8px"
            }),
        !isEpicCol &&
            css({
                backgroundColor: "transparent",
                border: "none",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                width: "100%",
                borderRadius: "4px",
                transition: "transform 1s ease-in",
                maxWidth: "406px",
                gap: "8px"
            })
    ];
    const readOnlyContent = (
        <>
            <HiddenAriaDescription
                id={activateToToggleMessageId}
                message={activateToEditTextDescription}
            />
            <button
                css={readOnlyContainerStyles}
                onClick={toggleEditMode}
                data-testid="read-only-story-row-item"
                aria-describedby={activateToToggleMessageId}
                aria-disabled={!isDemoComplete || editModeItemId != null}
            >
                <Story16IconCustom
                    primaryColor="#82B536"
                    label={storyLabelText}
                />
                <Name>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(readOnlyTextValue)
                        }}
                    />
                </Name>
            </button>
        </>
    );

    const editContent = (
        <EditContainer data-testid="edit-story-row-item" ref={containerRef}>
            <IconBlock>
                <Story16IconCustom
                    primaryColor="#82B536"
                    label={storyLabelText}
                />
            </IconBlock>
            <Input
                data-testid="edit-input-story-row-item"
                ref={inputRef}
                value={storyNameInputValue}
                onChange={(changeEvent) =>
                    setStoryInputName(changeEvent.target.value)
                }
                placeholder={intl.formatMessage(
                    messages.updateStoryPlaceholder
                )}
                onKeyDown={(keyEvent) => {
                    if (
                        keyEvent.key === "Enter" &&
                        storyNameInputValue.length > 0
                    ) {
                        updateStoryName(storyNameInputValue);
                        keyEvent.preventDefault();
                    }
                }}
                onBlur={() => {
                    if (storyNameInputValue.length > 0) {
                        updateStoryName(storyNameInputValue);
                    }
                }}
            />
        </EditContainer>
    );

    return rowMode === "EDIT" ? editContent : readOnlyContent;
};

export type EpicColumnRowPropsType = {
    id: string;
    name: string | MessageDescriptor;
    storyIndex: number;
    epicIndex: number;
    isEpicCol?: boolean;
};

export type ContentMode = "EDIT" | "VIEW";
